import React, { useCallback, useEffect, useState } from 'react';
import { PwaInstallSection } from '../components/PwaInstallSection';
import { pwaConfig } from '../config/pwaConfig';
import { usePwaInstallPrompt } from '../hooks/usePwaInstallPrompt';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { accountsAPI } from '../services/api';
import { getUser } from '../services/auth';
import { cacheUtils } from '../utils/cache';
import { resolveApiErrorMessage } from '../utils/apiErrors';
import { Briefcase, Calendar, IdCard, MapPin, Phone, RefreshCw, User, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useLocalization } from '../context/LocalizationContext';

const parseProfilePayload = (payload) => {
  if (!payload) return {};
  if (payload.value && typeof payload.value === 'object') return payload.value;
  if (typeof payload.value === 'string') {
    try {
      const parsed = JSON.parse(payload.value);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_e) {
      return {};
    }
  }
  return payload && typeof payload === 'object' ? payload : {};
};

const readValue = (value, fallback = 'Not available') => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text ? text : fallback;
};

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  return Boolean(String(value).trim());
};

const readProfileValue = (profile, keys = [], fallback = 'Not available') => {
  for (const key of keys) {
    const value = profile?.[key];
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return fallback;
};

export const Profile = () => {
  const { locale, needsCompactFont, t } = useLocalization();
  const {
    canPromptInstall,
    isInstalled,
    isIosSafari,
    promptInstall,
  } = usePwaInstallPrompt();
  const user = getUser() || {};
  const memberGl = String(user.memberGl || user.customerId || user.username || '').trim();
  const CACHE_KEY = `member_profile_${memberGl}`;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [timeTick, setTimeTick] = useState(0);

  const fetchProfile = useCallback(
    async (showLoading = true) => {
      if (!memberGl) {
        setErrorMessage(t('profile.memberUnavailable'));
        setLoading(false);
        return;
      }

      if (showLoading) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setErrorMessage('');
      try {
        const response = await accountsAPI.getMemberProfile(memberGl);
        const parsed = parseProfilePayload(response?.data);
        setProfile(parsed);
        cacheUtils.setCache(CACHE_KEY, parsed);
        setLastUpdated(Date.now());
        if (!showLoading) {
          toast.success(t('profile.refreshSuccess'));
        }
      } catch (error) {
        const message = resolveApiErrorMessage(error, t, t('profile.failedToLoad'));
        const hasCachedProfile = Boolean(cacheUtils.getCache(CACHE_KEY));
        if (hasCachedProfile) {
          setErrorMessage(t('profile.refreshFailedCached'));
          toast.error(message);
        } else {
          setErrorMessage(message);
          toast.error(message);
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [CACHE_KEY, memberGl, t]
  );

  useEffect(() => {
    const cached = cacheUtils.getCache(CACHE_KEY);
    if (cached && typeof cached === 'object') {
      setProfile(cached);
      setLastUpdated(cacheUtils.getCacheTimestamp(CACHE_KEY));
      setLoading(false);
      return;
    }
    fetchProfile(true);
  }, [CACHE_KEY, fetchProfile]);

  useEffect(() => {
    if (!lastUpdated) return undefined;
    const timerId = window.setInterval(() => setTimeTick((value) => value + 1), 60000);
    return () => window.clearInterval(timerId);
  }, [lastUpdated]);

  const fullName = readValue(profile?.memberName || user?.name);
  const mobileNumber = readValue(profile?.mobileNumber || user?.mobileNumber);
  const memberDob = readProfileValue(profile, ['memberDOB', 'dob', 'dateOfBirth']);
  const employeeId = readProfileValue(profile, ['empId', 'employeeId', 'employeeID', 'empID']);
  const jobJoinedDate = readProfileValue(profile, ['jobJoinedDate']);
  const lastDrawnBasic = readProfileValue(profile, ['netPay', 'lastDrawnBasic']);
  const nomineeName = readProfileValue(profile, ['nomineeName']);
  const nomineeRelation = readProfileValue(profile, ['nomineeRelation']);
  const rawPlaceOfWork = readProfileValue(profile, ['placeOfWork'], '');
  const placeOfWork = readValue(rawPlaceOfWork);
  const address = readProfileValue(profile, ['address', 'memberAddress', 'communicationAddress']);
  const shouldShowEmploymentFields = hasMeaningfulValue(rawPlaceOfWork);
  const nomineeDisplay =
    nomineeName === 'Not available'
      ? nomineeName
      : nomineeRelation === 'Not available'
        ? nomineeName
        : `${nomineeName} (${nomineeRelation})`;

  return (
    <DashboardLayout>
      <div className="app-page-shell max-w-5xl space-y-6 sm:space-y-7 fade-in" data-testid="profile-page">
        <div className="pointer-events-none absolute -z-10 inset-0 dark:hidden">
          <div className="absolute -top-8 -left-8 h-44 w-44 rounded-full bg-primary-200/35 blur-3xl" />
          <div className="absolute top-24 right-0 h-48 w-48 rounded-full bg-primary-200/30 blur-3xl" />
        </div>

        <div className="flex items-center justify-between gap-3 pb-1 sm:pb-2">
          <div>
            <h1
              className={`app-page-heading ${needsCompactFont ? 'app-page-heading-te' : ''}`}
            >
              {t('profile.title')}
            </h1>
            {lastUpdated && (
              <p className="app-page-meta">
                {t('common.lastUpdated', { time: cacheUtils.getTimeAgo(lastUpdated, locale, t) })}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              cacheUtils.clearCache(CACHE_KEY);
              fetchProfile(false);
            }}
            disabled={loading || refreshing}
            className="app-primary-button"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            <span>{refreshing ? t('common.refreshing') : t('common.refresh')}</span>
          </button>
        </div>

        {loading ? (
          <div className="app-surface p-10">
            <div className="flex items-center justify-center gap-3 text-primary-800 dark:text-primary-200">
              <RefreshCw size={20} className="animate-spin text-primary-500" />
              <span>{t('profile.loading')}</span>
            </div>
          </div>
        ) : (
          <>
            {!!errorMessage && (
              <div className="rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3">
                <p className="text-sm text-red-700 dark:text-red-200">{errorMessage}</p>
              </div>
            )}

            <div className="app-hero-surface mt-1 p-5 sm:mt-2 sm:p-6">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-full bg-white/20 border border-white/30 flex items-center justify-center">
                  <User size={28} />
                </div>
                <div>
                  <p className="text-primary-100 text-sm">{t('common.member')}</p>
                  <h2 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight">{fullName}</h2>
                  <p className="text-primary-100 text-sm mt-0.5">{t('profile.glId', { id: readValue(profile?.glId || memberGl, 'N/A') })}</p>
                </div>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-4 sm:mt-4 sm:grid-cols-2 sm:gap-5">
              <div className="app-surface p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 text-primary-500">
                    <Phone size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wide text-primary-600 dark:text-primary-300">{t('profile.mobileNumber')}</p>
                    <p className="text-sm sm:text-base font-semibold text-primary-900 dark:text-white mt-1 break-all">
                      {mobileNumber}
                    </p>
                  </div>
                </div>
              </div>

              <div className="app-surface p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 text-primary-500">
                    <Calendar size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wide text-primary-600 dark:text-primary-300">{t('profile.dateOfBirth')}</p>
                    <p className="text-sm sm:text-base font-semibold text-primary-900 dark:text-white mt-1">
                      {memberDob}
                    </p>
                  </div>
                </div>
              </div>

              {shouldShowEmploymentFields && (
                <div className="app-surface p-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 text-primary-500">
                      <IdCard size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wide text-primary-600 dark:text-primary-300">{t('profile.employeeId')}</p>
                      <p className="text-sm sm:text-base font-semibold text-primary-900 dark:text-white mt-1 break-words">
                        {employeeId}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {shouldShowEmploymentFields && (
                <div className="app-surface p-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 text-primary-500">
                      <Calendar size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wide text-primary-600 dark:text-primary-300">{t('profile.jobJoinedDate')}</p>
                      <p className="text-sm sm:text-base font-semibold text-primary-900 dark:text-white mt-1 break-words">
                        {jobJoinedDate}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {shouldShowEmploymentFields && (
                <div className="app-surface p-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 text-primary-500">
                      <Briefcase size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wide text-primary-600 dark:text-primary-300">{t('profile.lastDrawnBasic')}</p>
                      <p className="text-sm sm:text-base font-semibold text-primary-900 dark:text-white mt-1 break-words">
                        {lastDrawnBasic}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="app-surface p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 text-primary-500">
                    <Users size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wide text-primary-600 dark:text-primary-300">{t('profile.nominee')}</p>
                    <p className="text-sm sm:text-base font-semibold text-primary-900 dark:text-white mt-1 leading-relaxed break-words">
                      {nomineeDisplay}
                    </p>
                  </div>
                </div>
              </div>

              {shouldShowEmploymentFields && (
                <div className="app-surface p-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 text-primary-500">
                      <Briefcase size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wide text-primary-600 dark:text-primary-300">{t('profile.placeOfWork')}</p>
                      <p className="text-sm sm:text-base font-semibold text-primary-900 dark:text-white mt-1 leading-relaxed break-words">
                        {placeOfWork}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="app-surface p-4 sm:col-span-2">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 text-primary-500">
                    <MapPin size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wide text-primary-600 dark:text-primary-300">{t('profile.address')}</p>
                    <p className="text-sm sm:text-base font-semibold text-primary-900 dark:text-white mt-1 leading-relaxed">
                      {address}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {pwaConfig.profileInstallSection && (canPromptInstall || isInstalled || (pwaConfig.iosInstallGuide && isIosSafari)) && (
              <PwaInstallSection
                canPromptInstall={canPromptInstall}
                isInstalled={isInstalled}
                isIosSafari={pwaConfig.iosInstallGuide && isIosSafari}
                onInstall={promptInstall}
              />
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Profile;
